/**
 * The call-waiting tone: two short beeps in the ear of a person who is
 * already talking, not the full ring.
 *
 * The shape is the European one (ETSI): 425 Hz, 0.2 s on, 0.2 s off, 0.2 s on,
 * then quiet until the next pair. It plays on the CALL output, not on the ring
 * device: the person is wearing the headset right now, and a ring on the room
 * speaker would be heard by everybody except them.
 *
 * Like the ring it may stay silent (no audio context, a refused device), and
 * the knock card is the real notice. The shared context is used and never
 * closed - see `audio/context.ts`.
 */
import { useEffect } from "react";
import { openAudioSink } from "../audio/sink";
import { readAudioChoice } from "../audio/choice";
import { readAudioVolume } from "../audio/volume";
import { readyAudioContext } from "../audio/context";

export const KNOCK_HZ = 425;
/** Start of each beep within one pair, and how long it sounds. */
export const KNOCK_BEEPS: readonly { at: number; dur: number }[] = [
  { at: 0, dur: 0.2 },
  { at: 0.4, dur: 0.2 },
];
/** One pair every four seconds: present, but not over the conversation. */
export const KNOCK_CYCLE_SEC = 4;
/** Quieter than the ring: it is mixed into an ear that is listening to a person. */
const KNOCK_GAIN = 0.05;
const RAMP_SEC = 0.015;

async function startKnockTone(deviceId: string): Promise<(() => void) | null> {
  const ctx = await readyAudioContext();
  if (!ctx) return null;
  const sink = await openAudioSink(ctx, deviceId);
  let stopped = false;

  const pair = () => {
    if (stopped || ctx.state !== "running") return;
    // The ring slider governs every alert sound; read per pair so a change
    // made while the knock is up takes effect on the next beep.
    const peak = KNOCK_GAIN * readAudioVolume().ring;
    if (peak <= 0) return;
    const now = ctx.currentTime;
    for (const beep of KNOCK_BEEPS) {
      const at = now + beep.at;
      const gain = ctx.createGain();
      gain.connect(sink.node);
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(peak, at + RAMP_SEC);
      gain.gain.setValueAtTime(peak, at + beep.dur - RAMP_SEC);
      gain.gain.linearRampToValueAtTime(0, at + beep.dur);
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(KNOCK_HZ, at);
      osc.connect(gain);
      osc.start(at);
      osc.stop(at + beep.dur);
    }
  };

  pair();
  const timer = window.setInterval(pair, KNOCK_CYCLE_SEC * 1000);
  return () => {
    stopped = true;
    window.clearInterval(timer);
    sink.dispose();
  };
}

/** Beep while `active` is true. */
export function useKnockTone(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let stop: (() => void) | null = null;
    void startKnockTone(readAudioChoice().output)
      .then((s) => {
        if (cancelled) s?.();
        else stop = s;
      })
      .catch(() => {
        // No sound. The card carries the knock.
      });
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [active]);
}
